import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import apiClient, { API_ENDPOINTS, BASE_URL } from '../../config/api';

const emptyCompanyForm = {
  name: '',
  address: '',
  phone: '',
  email: '',
};

const ManageCompany = ({ setActiveScreen }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [togglingShopModeId, setTogglingShopModeId] = useState(null);

  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const [adminFormData, setAdminFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
  });

  const normalizeCompanies = (data) => {
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    return [];
  };

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(API_ENDPOINTS.BILLING_COMPANIES);
      setCompanies(normalizeCompanies(response.data));
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to fetch companies.');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const getLogoUrl = (logo) => {
    if (!logo) return null;
    if (typeof logo === 'string' && (logo.startsWith('http://') || logo.startsWith('https://'))) {
      return logo;
    }
    const path = typeof logo === 'string' ? logo : '';
    return path.startsWith('/') ? `${BASE_URL}${path}` : `${BASE_URL}/${path}`;
  };

  const parseApiErrors = (err, fallback) => {
    const data = err?.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    const fieldErrors = Object.entries(data)
      .filter(([, v]) => Array.isArray(v) && v.length)
      .map(([k, v]) => `${k}: ${v[0]}`);
    if (fieldErrors.length) return fieldErrors.join('\n');
    return fallback;
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddCompany = () => {
    if (setActiveScreen) {
      setActiveScreen('create-company');
    }
  };

  const handleOpenEditModal = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
    });
    setLogoFile(null);
    setLogoPreview(getLogoUrl(company.logo));
    setShowCompanyModal(true);
  };

  const handleCloseCompanyModal = () => {
    setShowCompanyModal(false);
    setEditingCompany(null);
    setCompanyForm(emptyCompanyForm);
    setLogoFile(null);
    setLogoPreview(null);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleCompanyFormChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    } else if (editingCompany) {
      setLogoPreview(getLogoUrl(editingCompany.logo));
    } else {
      setLogoPreview(null);
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!editingCompany?.id) return;

    if (!companyForm.name || !companyForm.address || !companyForm.phone || !companyForm.email) {
      Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Please fill in all required fields.' });
      return;
    }

    setSavingCompany(true);
    try {
      const endpoint = API_ENDPOINTS.BILLING_COMPANY_DETAIL(editingCompany.id);

      if (logoFile) {
        const data = new FormData();
        data.append('name', companyForm.name);
        data.append('address', companyForm.address);
        data.append('phone', companyForm.phone);
        data.append('email', companyForm.email);
        data.append('logo', logoFile);
        await apiClient.put(endpoint, data);
      } else {
        await apiClient.put(endpoint, {
          name: companyForm.name,
          address: companyForm.address,
          phone: companyForm.phone,
          email: companyForm.email,
        });
      }

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: `${companyForm.name} was updated successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });

      handleCloseCompanyModal();
      fetchCompanies();
    } catch (err) {
      console.error('Error updating company:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: parseApiErrors(err, 'Failed to update company. Please try again.'),
      });
    } finally {
      setSavingCompany(false);
    }
  };

  const handleToggleSmallShopMode = async (company) => {
    if (!company?.id) return;

    const nextMode = !company.small_shop_mode;
    setTogglingShopModeId(company.id);

    // Optimistic UI update
    setCompanies((prev) =>
      prev.map((c) => (c.id === company.id ? { ...c, small_shop_mode: nextMode } : c))
    );

    try {
      const endpoint = API_ENDPOINTS.BILLING_COMPANY_DETAIL(company.id);
      const res = await apiClient.patch(endpoint, { small_shop_mode: nextMode });

      const serverValue =
        typeof res?.data?.small_shop_mode === 'boolean' ? res.data.small_shop_mode : nextMode;

      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, small_shop_mode: serverValue } : c))
      );
    } catch (err) {
      // Revert on failure
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, small_shop_mode: !nextMode } : c))
      );

      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: parseApiErrors(err, 'Failed to update small shop mode.'),
      });
    } finally {
      setTogglingShopModeId(null);
    }
  };

  const handleDeleteCompany = async (company) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete company?',
      html: `This will permanently remove <strong>${company.name}</strong> and related data.`,
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    setDeletingId(company.id);
    try {
      await apiClient.delete(API_ENDPOINTS.BILLING_COMPANY_DETAIL(company.id));
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: `${company.name} has been removed.`,
        timer: 2000,
        showConfirmButton: false,
      });
      fetchCompanies();
    } catch (err) {
      console.error('Error deleting company:', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete failed',
        text: parseApiErrors(err, 'Failed to delete company. Please try again.'),
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenAdminModal = (company) => {
    setSelectedCompany(company);
    setAdminFormData({
      first_name: '',
      last_name: '',
      email: '',
      is_active: true,
    });
    setShowAdminModal(true);
  };

  const handleAdminFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAdminFormData({
      ...adminFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreatingAdmin(true);

    try {
      const payload = {
        email: adminFormData.email,
        role: 'admin',
        first_name: adminFormData.first_name,
        last_name: adminFormData.last_name,
        is_active: adminFormData.is_active,
        company_id: selectedCompany?.id,
      };

      await apiClient.post(API_ENDPOINTS.CREATE_USER, payload);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Admin created successfully for ${selectedCompany?.name}! A temporary password has been sent to their email.`,
        confirmButtonText: 'OK',
      });

      setShowAdminModal(false);
    } catch (err) {
      console.error('Error creating admin:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: parseApiErrors(err, 'Failed to create admin. Please try again.'),
      });
    } finally {
      setCreatingAdmin(false);
    }
  };

  const filteredCompanies = companies.filter((company) => {
    const term = searchTerm.toLowerCase();
    return (
      (company.name && company.name.toLowerCase().includes(term)) ||
      (company.email && company.email.toLowerCase().includes(term)) ||
      (company.phone && company.phone.includes(term)) ||
      (company.address && company.address.toLowerCase().includes(term))
    );
  });

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const avatarColors = [
    '#4154f1', '#2eca6a', '#ff771d', '#012970', '#17a2b8', '#6f42c1', '#e83e8c', '#20c997',
  ];

  const getAvatarStyle = (index) => ({
    background: `linear-gradient(135deg, ${avatarColors[index % avatarColors.length]}, ${avatarColors[(index + 3) % avatarColors.length]})`,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.75rem',
    fontWeight: '700',
    height: '96px',
    width: '96px',
    borderRadius: '50%',
    margin: '0 auto',
    boxShadow: '0 8px 24px rgba(65, 84, 241, 0.25)',
  });

  const logoStyle = {
    height: '96px',
    width: '96px',
    objectFit: 'cover',
    borderRadius: '50%',
    margin: '0 auto',
    display: 'block',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    border: '4px solid #fff',
    backgroundColor: '#fff',
  };

  return (
    <>
      <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h1 style={{ fontWeight: 700, color: '#012970' }}>Manage Companies</h1>
          <nav>
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item">System Admin</li>
              <li className="breadcrumb-item active">Manage Company</li>
            </ol>
          </nav>
        </div>
        <button
          type="button"
          className="btn btn-primary shadow-sm"
          style={{ borderRadius: '8px', fontWeight: '600' }}
          onClick={handleAddCompany}
        >
          <i className="bi bi-plus-lg me-2" />
          Add Company
        </button>
      </div>

      <section className="section">
        {!loading && !error && (
          <div
            className="d-flex flex-wrap align-items-center gap-2 mb-3 px-2 py-2"
            style={{
              background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)',
              border: '1px solid #e8ecf4',
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(1, 39, 112, 0.06)',
            }}
          >
            <div
              className="d-inline-flex align-items-center gap-2 px-2 py-1"
              style={{
                background: 'linear-gradient(135deg, #4154f1, #012970)',
                borderRadius: '20px',
                fontSize: '0.75rem',
                color: '#fff',
                lineHeight: 1.2,
              }}
            >
              <i className="bi bi-buildings" style={{ fontSize: '0.85rem', opacity: 0.9 }} />
              <span className="fw-bold" style={{ fontSize: '0.95rem' }}>{companies.length}</span>
              <span style={{ opacity: 0.85, fontSize: '0.7rem' }}>total</span>
            </div>

            <div
              className="d-inline-flex align-items-center gap-2 px-2 py-1"
              style={{
                background: '#fff',
                border: '1px solid #dce3f0',
                borderRadius: '20px',
                fontSize: '0.75rem',
                color: '#012970',
                lineHeight: 1.2,
              }}
            >
              <i className="bi bi-funnel text-primary" style={{ fontSize: '0.8rem' }} />
              <span className="fw-bold text-primary" style={{ fontSize: '0.95rem' }}>{filteredCompanies.length}</span>
              <span className="text-muted" style={{ fontSize: '0.7rem' }}>shown</span>
            </div>

            <div className="flex-grow-1" style={{ minWidth: '140px', maxWidth: '280px' }}>
              <div className="position-relative">
                <i
                  className="bi bi-search position-absolute text-muted"
                  style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', pointerEvents: 'none' }}
                />
                <input
                  type="text"
                  className="form-control form-control-sm border-0 bg-white"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearch}
                  style={{
                    paddingLeft: '28px',
                    paddingRight: searchTerm ? '28px' : '10px',
                    height: '28px',
                    fontSize: '0.8rem',
                    borderRadius: '20px',
                    boxShadow: 'inset 0 0 0 1px #e0e6ef',
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="btn btn-link position-absolute p-0 text-muted"
                    style={{ right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.7rem', lineHeight: 1 }}
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <i className="bi bi-x-circle-fill" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted fw-bold">Loading companies...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger shadow-sm d-flex align-items-center justify-content-between" style={{ borderRadius: '12px' }}>
            <span><i className="bi bi-exclamation-triangle-fill me-2" /> {error}</span>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchCompanies}>
              Retry
            </button>
          </div>
        ) : filteredCompanies.length > 0 ? (
          <div className="row">
            {filteredCompanies.map((company, index) => {
              const logoUrl = getLogoUrl(company.logo);
              return (
                <div className="col-xl-4 col-lg-6 mb-4" key={company.id}>
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{
                      borderRadius: '16px',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = '0 16px 32px rgba(1, 39, 112, 0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 0.125rem 0.25rem rgba(0,0,0,0.075)';
                    }}
                  >
                    <div
                      style={{
                        height: '6px',
                        background: `linear-gradient(90deg, ${avatarColors[index % avatarColors.length]}, ${avatarColors[(index + 2) % avatarColors.length]})`,
                      }}
                    />
                    <div className="card-body text-center pt-4 pb-2 px-4">
                      <div className="mb-3 position-relative d-inline-block">
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={`${company.name} logo`}
                            style={logoStyle}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{ ...getAvatarStyle(index), display: logoUrl ? 'none' : 'flex' }}>
                          {getInitials(company.name)}
                        </div>
                      </div>

                      <h5 className="mb-1" style={{ color: '#012970', fontWeight: '800' }}>
                        {company.name}
                      </h5>
                     
                      {company.small_shop_mode && (
                        <span className="badge rounded-pill text-bg-info mb-3">
                          <i className="bi bi-shop me-1" />
                          Small shop mode
                        </span>
                      )}

                      <ul className="list-group list-group-flush text-start mt-3 rounded-3 border">
                        <li className="list-group-item d-flex align-items-start gap-2 border-0 py-2 px-0">
                          <i className="bi bi-envelope text-primary mt-1" />
                          <div className="flex-grow-1 min-w-0">
                            <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}>Email</small>
                            <span className="fw-semibold text-break">{company.email || '—'}</span>
                          </div>
                        </li>
                        <li className="list-group-item d-flex align-items-start gap-2 border-0 py-2 px-0">
                          <i className="bi bi-telephone text-success mt-1" />
                          <div>
                            <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}>Phone</small>
                            <span className="fw-semibold">{company.phone || '—'}</span>
                          </div>
                        </li>
                        <li className="list-group-item d-flex align-items-start gap-2 border-0 py-2 px-0">
                          <i className="bi bi-geo-alt text-danger mt-1" />
                          <div>
                            <small className="text-muted d-block text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}>Address</small>
                            <span className="fw-semibold">{company.address || '—'}</span>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <div className="card-footer bg-transparent border-0 pt-0 pb-4 px-4">
                      <div className="d-grid gap-2">
                        <div
                          className="d-flex align-items-center justify-content-between px-2 py-2"
                          style={{
                            border: '1px solid #e8ecf4',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)',
                          }}
                        >
                          <div className="d-flex flex-column">
                            <span className="fw-semibold" style={{ color: '#012970', fontSize: '0.9rem' }}>
                              Small shop mode
                            </span>
                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                              Admin & Cashier only
                            </small>
                          </div>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id={`small_shop_mode_${company.id}`}
                              checked={!!company.small_shop_mode}
                              disabled={togglingShopModeId === company.id}
                              onChange={() => handleToggleSmallShopMode(company)}
                            />
                          </div>
                        </div>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleOpenEditModal(company)}
                          >
                            <i className="bi bi-pencil-square me-1" />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleOpenAdminModal(company)}
                          >
                            <i className="bi bi-person-plus me-1" />
                            Admin
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={deletingId === company.id}
                            onClick={() => handleDeleteCompany(company)}
                          >
                            {deletingId === company.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <>
                                <i className="bi bi-trash me-1" />
                                Delete
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-5">
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
              style={{ width: '100px', height: '100px', background: 'linear-gradient(135deg, #e8ecff, #f6f9ff)' }}
            >
              <i className="bi bi-building text-primary" style={{ fontSize: '2.5rem' }} />
            </div>
            <h4 className="fw-bold text-dark mb-2">
              {companies.length === 0 ? 'No companies yet' : 'No matches found'}
            </h4>
            <p className="text-muted mb-4">
              {companies.length === 0
                ? 'Create your first company to get started.'
                : 'Try a different search term or clear the filter.'}
            </p>
            {companies.length === 0 ? (
              <button type="button" className="btn btn-primary rounded-pill px-4" onClick={handleAddCompany}>
                <i className="bi bi-plus-lg me-2" />
                Add Company
              </button>
            ) : (
              <button type="button" className="btn btn-outline-primary rounded-pill px-4" onClick={() => setSearchTerm('')}>
                Clear search
              </button>
            )}
          </div>
        )}
      </section>

      {/* Edit Company Modal */}
      {showCompanyModal && editingCompany && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold" style={{ color: '#012970' }}>
                  Edit Company
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseCompanyModal} aria-label="Close" />
              </div>
              <form onSubmit={handleUpdateCompany}>
                <div className="modal-body pt-2">
                  <div className="row g-3">
                    <div className="col-md-4 text-center">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="rounded-circle mb-3"
                          style={{ width: '100px', height: '100px', objectFit: 'cover', border: '3px solid #eee' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 text-white fw-bold"
                          style={{ width: '100px', height: '100px', background: '#4154f1', fontSize: '1.5rem' }}
                        >
                          {getInitials(companyForm.name)}
                        </div>
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        className="form-control form-control-sm"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      <small className="text-muted d-block mt-1">Leave empty to keep current logo</small>
                    </div>
                    <div className="col-md-8">
                      <div className="mb-3">
                        <label htmlFor="edit_name" className="form-label fw-semibold">Company Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          id="edit_name"
                          name="name"
                          value={companyForm.name}
                          onChange={handleCompanyFormChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="edit_email" className="form-label fw-semibold">Email *</label>
                        <input
                          type="email"
                          className="form-control"
                          id="edit_email"
                          name="email"
                          value={companyForm.email}
                          onChange={handleCompanyFormChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="edit_phone" className="form-label fw-semibold">Phone *</label>
                        <input
                          type="tel"
                          className="form-control"
                          id="edit_phone"
                          name="phone"
                          value={companyForm.phone}
                          onChange={handleCompanyFormChange}
                          required
                        />
                      </div>
                      <div className="mb-0">
                        <label htmlFor="edit_address" className="form-label fw-semibold">Address *</label>
                        <input
                          type="text"
                          className="form-control"
                          id="edit_address"
                          name="address"
                          value={companyForm.address}
                          onChange={handleCompanyFormChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-light" onClick={handleCloseCompanyModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={savingCompany}>
                    {savingCompany ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg me-1" />
                        Save changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Admin Modal */}
      {showAdminModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px' }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold" style={{ color: '#012970' }}>
                  Create Admin
                  <small className="d-block text-muted fw-normal mt-1">for {selectedCompany?.name}</small>
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowAdminModal(false)} aria-label="Close" />
              </div>
              <form onSubmit={handleCreateAdmin}>
                <div className="modal-body">
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#e8f4fc', borderRadius: '10px' }}>
                    <i className="bi bi-info-circle me-2" />
                    A temporary password will be sent to the email address.
                  </div>
                  <div className="mb-3">
                    <label htmlFor="first_name" className="form-label fw-semibold">First Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="first_name"
                      name="first_name"
                      value={adminFormData.first_name}
                      onChange={handleAdminFormChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="last_name" className="form-label fw-semibold">Last Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      id="last_name"
                      name="last_name"
                      value={adminFormData.last_name}
                      onChange={handleAdminFormChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="admin_email" className="form-label fw-semibold">Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      id="admin_email"
                      name="email"
                      value={adminFormData.email}
                      onChange={handleAdminFormChange}
                      required
                    />
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is_active"
                      name="is_active"
                      checked={adminFormData.is_active}
                      onChange={handleAdminFormChange}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Active (user can log in immediately)
                    </label>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-light" onClick={() => setShowAdminModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={creatingAdmin}>
                    {creatingAdmin ? 'Creating...' : 'Create Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageCompany;
